import { useQuery } from '@tanstack/react-query'
import { type PaginationState } from '@tanstack/react-table'

import { fetchWithHeaders } from 'utils'
import { type ChargeOrTxn } from 'utils/web_api.schemas'

import { GrapeAPIError, type GrapeAPIErrorType } from '../GrapeAPIError'
import { type PaginatedResponse } from '../types'

type GetTransactionsForEnrollmentQueryArgs = {
  memberId: string
  enrollmentId: string
  pagination: PaginationState
}

export function getTransactionsForEnrollmentQueryKey ({
  memberId,
  enrollmentId,
  pagination,
}: GetTransactionsForEnrollmentQueryArgs) {
  return ['brokers', 'members', memberId, 'transactions', enrollmentId, 'pagination', pagination]
}

export function useGetTransactionsForEnrollment ({
  memberId,
  enrollmentId,
  pagination,
}: GetTransactionsForEnrollmentQueryArgs) {
  return useQuery<PaginatedResponse<ChargeOrTxn>, GrapeAPIErrorType>({
    queryKey: getTransactionsForEnrollmentQueryKey({ memberId, enrollmentId, pagination }),
    queryFn: async () => {
      const pageIndex = pagination.pageIndex.toString()
      const queryParams: Record<string, any> = {
        'page[number]': pageIndex,
        'page[size]': pagination.pageSize.toString(),
      }
      if (enrollmentId) {
        queryParams['filter[enrollment_id]'] = enrollmentId
      }
      const queryString = new URLSearchParams(queryParams).toString()

      const response = await fetchWithHeaders(
        `/api/web/brokers/members/${memberId}/transactions?${queryString}`
      )
      if (!response.ok) {
        const errorJson = await response.json()
        throw GrapeAPIError({
          status: response.status,
          message: errorJson.message,
          errors: errorJson.errors,
        })
      }
      return response.json()
    },
    retry: false,
  })
}
